import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Truck, Pencil, Eye } from 'lucide-react';
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import DocumentModal from '../common/DocumentModal.js';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

export default function ViewVehicle() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState(null);

  const openDocumentModal = (docType) => {
    setCurrentDocumentType(docType);
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
    setCurrentDocumentType(null);
  };

  const getDocumentUrl = () => {
    if (!currentDocumentType || !vehicle) return null;

    let base64Data = null;

    switch (currentDocumentType) {
      case 'policy_document':
        base64Data = vehicle.policy_document_base64;
        break;
      case 'vehicle_rc':
        base64Data = vehicle.vehicle_rc_base64;
        break;
      case 'puc':
        base64Data = vehicle.puc_base64;
        break;
      case 'purchase_invoice':
        base64Data = vehicle.purchase_invoice_base64;
        break;
      default:
        return null;
    }

    if (base64Data) {
      const isPDF = base64Data.startsWith('JVBERi0');
      const mimeType = isPDF ? 'application/pdf' : 'image/jpeg';
      return `data:${mimeType};base64,${base64Data}`;
    }

    return null;
  };

  const getDocumentTitle = () => {
    switch (currentDocumentType) {
      case 'policy_document':
        return 'Policy Document';
      case 'vehicle_rc':
        return 'Vehicle RC';
      case 'puc':
        return 'PUC Certificate';
      case 'purchase_invoice':
        return 'Purchase Invoice';
      default:
        return 'Document';
    }
  };

  useEffect(() => {
    fetchVehicleDetails();
  }, [vehicleId]);

  const fetchVehicleDetails = async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getVehicleById(vehicleId);
      setVehicle(response.data.data);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load vehicle details');
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <PageLayout title="Vehicle Details" icon={Truck} maxWidth="max-w-4xl">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading vehicle details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!vehicle) {
    return (
      <PageLayout title="Vehicle Details" icon={Truck} maxWidth="max-w-4xl">
        <DetailCard>
          <p className="text-center text-gray-600">Vehicle not found</p>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => navigate('/vehicles')}>
              Back to Vehicles
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    );
  }

  const hasInsurance =
    vehicle.insurance_provider || vehicle.agent_name || vehicle.policy_no;

  const documents = [
    { key: 'policy_document', label: 'Policy Document', data: vehicle.policy_document_base64 },
    { key: 'vehicle_rc', label: 'Vehicle RC', data: vehicle.vehicle_rc_base64 },
    { key: 'puc', label: 'PUC Certificate', data: vehicle.puc_base64 },
    { key: 'purchase_invoice', label: 'Purchase Invoice', data: vehicle.purchase_invoice_base64 },
  ].filter((doc) => doc.data);

  return (
    <>
      <PageLayout
        title={vehicle.vehicle_name}
        icon={Truck}
        maxWidth="max-w-4xl"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/vehicles')}>
              Back to List
            </Button>
            <Button icon={Pencil} onClick={() => navigate(`/vehicles/edit/${vehicle.id}`)}>
              Edit Vehicle
            </Button>
          </>
        }
      >
        <div className="mb-4 flex items-center gap-3">
          <span className="text-gray-600 font-mono">{vehicle.vehicle_number}</span>
          <StatusBadge status={vehicle.status} />
        </div>

        <DetailCard title="Vehicle Information" className="mb-4">
          <DetailRow label="Vehicle Name" value={vehicle.vehicle_name} />
          <DetailRow label="Vehicle Number" value={vehicle.vehicle_number} />
          <DetailRow
            label="Vehicle Type"
            value={vehicle.vehicle_type.replace(/_/g, ' ').toUpperCase()}
          />
          <DetailRow label="Status" value={<StatusBadge status={vehicle.status} />} />
          <DetailRow
            label="Contract Amount"
            value={
              vehicle.vehicle_contract_amount != null
                ? `₹${Number(vehicle.vehicle_contract_amount).toLocaleString('en-IN')}`
                : '-'
            }
          />
        </DetailCard>

        {hasInsurance && (
          <DetailCard title="Insurance Information" className="mb-4">
            {vehicle.insurance_provider && (
              <DetailRow label="Insurance Provider" value={vehicle.insurance_provider} />
            )}
            {vehicle.agent_name && <DetailRow label="Agent Name" value={vehicle.agent_name} />}
            {vehicle.agent_no && <DetailRow label="Agent Number" value={vehicle.agent_no} />}
            {vehicle.policy_no && <DetailRow label="Policy Number" value={vehicle.policy_no} />}
            {vehicle.policy_start_date && (
              <DetailRow label="Policy Start Date" value={formatDate(vehicle.policy_start_date)} />
            )}
            {vehicle.policy_expiry_date && (
              <DetailRow label="Policy Expiry Date" value={formatDate(vehicle.policy_expiry_date)} />
            )}
            {vehicle.insured_declared_value && (
              <DetailRow
                label="Insured Declared Value"
                value={`₹${vehicle.insured_declared_value.toLocaleString('en-IN')}`}
              />
            )}
          </DetailCard>
        )}

        {documents.length > 0 && (
          <DetailCard title="Documents" className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {documents.map((doc) => (
                <Button
                  key={doc.key}
                  variant="outline"
                  icon={Eye}
                  onClick={() => openDocumentModal(doc.key)}
                  className="w-full justify-center"
                >
                  View {doc.label}
                </Button>
              ))}
            </div>
          </DetailCard>
        )}

        <DetailCard title="Additional Information">
          <DetailRow label="Vehicle ID" value={`#${vehicle.id}`} />
          <DetailRow label="Created Date" value={formatDate(vehicle.created_at)} />
          <DetailRow label="Last Updated" value={formatDate(vehicle.updated_at)} />
        </DetailCard>
      </PageLayout>

      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeDocumentModal}
        documentUrl={getDocumentUrl()}
        documentType={currentDocumentType}
        title={getDocumentTitle()}
      />
    </>
  );
}
